import { type ElementType, type ComponentPropsWithoutRef, type ReactNode } from "react";

type ButtonVariant = "default" | "soft" | "link" | "light" | "brand";

type ButtonProps<T extends ElementType = "button"> = {
	as?: T;
	variant?: ButtonVariant;
	children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "variant">;

const variantStyles: Record<ButtonVariant, string> = {
	default: "bg-brand text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed",
	soft: "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed",
	link: "text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed",
	light: "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed",
	brand: "bg-brand text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed",
};

function Button<T extends ElementType = "button">({ as, variant = "default", className, children, ...props }: ButtonProps<T>) {
	const Component = as || "button";
	const baseStyles = "px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer";

	return (
		<Component className={`${baseStyles} ${variantStyles[variant]} ${className || ""}`} {...props}>
			{children}
		</Component>
	);
}

export default Button;
